import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import {
  Alert,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  default as React,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getActiveStaff } from "../api/staff";
import { resolveTodoIdByDate } from "../api/selectTodo";
import { fetchTodo, updateTask } from "../api/useTodo";

import Loading from "../components/Loading";
import PageHeader from "../components/layout/PageHeader";
import { TaskBody, sortTasks } from "../components/TodoBase";
import {
  getTaskStyle,
  groupListStyle,
  groupNameStyle,
} from "../components/style";
import { useConfig } from "../contexts/ConfigContext";

function UserSelect({ onChange, style, activeStaffList, task }) {
  const [selectedUserId, setSelectedUserId] = useState(task.completed_by || "");

  const handleUserChange = (event) => {
    const selectedValue = event.target.value;
    setSelectedUserId(selectedValue);
    onChange(selectedValue || null);
  };

  const completedByStaff = activeStaffList.find(
    (staff) => staff.id === task.completed_by
  );

  if (task.status === 1) {
    return (
      <Box sx={{ ...style }}>
        {completedByStaff ? (
          <Typography
            sx={{ height: "32px", display: "flex", alignItems: "center" }}
          >
            {`${completedByStaff.family_name}　${completedByStaff.given_name}`}
          </Typography>
        ) : task.completed_by ? (
          <Typography
            sx={{
              color: "grey",
              height: "32px",
              display: "flex",
              alignItems: "center",
            }}
          >
            削除済みのスタッフ
          </Typography>
        ) : (
          <Typography
            sx={{
              color: "grey",
              height: "32px",
              display: "flex",
              alignItems: "center",
            }}
          >
            不明
          </Typography>
        )}
      </Box>
    );
  } else {
    return (
      <Box sx={{ ...style }}>
        <Select
          value={selectedUserId}
          onChange={handleUserChange}
          displayEmpty
          inputProps={{ "aria-label": "Without label" }}
          size="small"
          sx={{ height: "36px", width: "180px" }}
        >
          <MenuItem value="">
            <em>担当者選択</em>
          </MenuItem>
          {activeStaffList.map((staff) => (
            <MenuItem key={staff.id} value={staff.id}>
              {`${staff.family_name}　${staff.given_name}`}
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  }
}

// エラーダイアログコンポーネント
function ErrorDialog({ open, onClose, errorMessage }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="error-dialog-title"
      aria-describedby="error-dialog-description"
    >
      <DialogTitle id="error-dialog-title">
        {"通信に失敗しました。もう１度試してください。"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="error-dialog-description">
          {errorMessage}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" autoFocus>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// 警告ダイアログコンポーネント
function WarningDialog({ open, onClose }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="warning-dialog-title"
      aria-describedby="warning-dialog-description"
    >
      <DialogTitle id="warning-dialog-title">{"あかんで"}</DialogTitle>
      <DialogContent>
        <DialogContentText
          id="warning-dialog-description"
          sx={{
            color: "black",
            fontSize: "16px",
          }}
        >
          この仕事をやったのは誰か選択してください
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary" autoFocus>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Task({
  task,
  todoPublicId,
  setTodo,
  activeStaffList,
  drawTopBorder,
  onQueueUpdate,
  onCompleteUpdate,
}) {
  const [isChecked, setIsChecked] = useState(task.status !== 0);
  const [completedBy, setCompletedBy] = useState(task.completed_by);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showWarning, setShowWarning] = useState(false);
  const [isEditingMemo, setIsEditingMemo] = useState(false);
  const [memoText, setMemoText] = useState(task.memo || "");
  const updateQueue = useRef([]);
  const config = useConfig();
  const enableAssigneeSelection =
    config?.features?.enableAssigneeSelection ?? false;
  const uploadImageEnabled = config?.features?.enableUploadImage ?? false;

  const processUpdateQueue = async () => {
    if (updateQueue.current.length === 0) return;

    const update = updateQueue.current[0];
    try {
      const response = await updateTask(todoPublicId, task.public_id, update);

      setTodo((prevTodo) => {
        const updatedTodo = JSON.parse(JSON.stringify(prevTodo));
        for (let taskGroup of updatedTodo.task_groups) {
          for (let t of taskGroup.tasks) {
            if (t.id === task.id) {
              // Object.assign(t, response); // 不具合の原因となりうるため、より安全な代入方法に変更
              t.status = response.status;
              t.memo = response.memo;
              t.completed_by = response.completed_by;
              t.completed_at = response.completed_at;
              t.updated_at = response.updated_at;
              t.updated_by = response.updated_by;
              break;
            }
          }
        }
        return sortTasks(updatedTodo);
      });

      updateQueue.current.shift();
      onCompleteUpdate();
      if (updateQueue.current.length > 0) {
        processUpdateQueue();
      }
    } catch (error) {
      console.error("Failed to update task:", error);
      setErrorMessage(`Failed to update task: ${error.message}`);
      setShowError(true);
      setIsChecked(task.status !== 0);
      setCompletedBy(task.completed_by);
      setMemoText(task.memo || "");
    }
  };

  const queueUpdate = (update) => {
    updateQueue.current.push(update);
    onQueueUpdate();
    if (updateQueue.current.length === 1) {
      processUpdateQueue();
    }
  };

  const handleCheckboxChange = () => {
    if (enableAssigneeSelection && !isChecked && !completedBy) {
      setShowWarning(true);
      return;
    }

    const newCheckedState = !isChecked;
    const updatedStatus = newCheckedState ? 1 : 0;
    const updatedCompletedBy =
      enableAssigneeSelection && newCheckedState ? completedBy : null;

    setIsChecked(newCheckedState);

    queueUpdate({
      status: updatedStatus,
      ...(enableAssigneeSelection ? { completed_by: updatedCompletedBy } : {}),
    });
  };

  const handleUserSelectChange = (newCompletedBy) => {
    setCompletedBy(newCompletedBy);

    if (enableAssigneeSelection) {
      queueUpdate({
        status: isChecked ? 1 : 0,
        completed_by: newCompletedBy,
      });
    } else {
      queueUpdate({
        status: isChecked ? 1 : 0,
      });
    }
  };

  const handleCloseError = () => {
    setShowError(false);
    setErrorMessage("");
    setIsChecked(task.status !== 0);
    setCompletedBy(task.completed_by);
    setMemoText(task.memo || "");
    const queuedCount = updateQueue.current.length;
    updateQueue.current = [];
    if (queuedCount > 0) {
      onCompleteUpdate(queuedCount);
    }
  };

  const handleCloseWarning = () => {
    setShowWarning(false);
  };

  /* ---------------- Memo Editing Handlers ---------------- */
  const handleEditMemoClick = () => {
    setIsEditingMemo(true);
    setMemoText(task.memo || "");
  };

  const handleSaveMemo = () => {
    setIsEditingMemo(false);
    if (memoText !== task.memo) {
      queueUpdate({ memo: memoText });
    }
  };

  const handleCancelMemo = () => {
    setIsEditingMemo(false);
    setMemoText(task.memo || "");
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const month = date.getMonth() + 1; // getMonth()は0-11を返すので、1を加える
    const day = date.getDate();
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${month}/${day} ${hours}:${minutes}`;
  };

  const taskStyle = getTaskStyle(drawTopBorder);

  return (
    <ListItem
      dense
      style={{
        ...taskStyle,
        padding: "4px 8px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "stretch",
        minHeight: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          flexGrow: 1,
          mr: 1,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: 30,
            minWidth: 30,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mr: 1,
            pt: 0.125,
          }}
        >
          <Box sx={{ pt: 0.125 }}>
            <Checkbox
              checked={isChecked}
              tabIndex={-1}
              onChange={handleCheckboxChange}
              size="small"
              sx={{
                p: 0,
              }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            minWidth: 0,
          }}
        >
          <TaskBody
            name={task.name}
            detail={task.detail}
            images={uploadImageEnabled ? task.images : []}
            recurrenceType={task.recurrence_type}
            weeklyDays={task.weekly_days}
            monthlyDays={task.monthly_days}
            compact={true}
          />
          <Box sx={{ mt: 0.25 }}>
            {isEditingMemo ? (
              <>
                <TextField
                  value={memoText}
                  onChange={(e) => setMemoText(e.target.value)}
                  placeholder="メモを記入することができます。"
                  multiline
                  minRows={2}
                  fullWidth
                  size="small"
                  autoFocus
                  sx={{
                    "& .MuiInputBase-root": {
                      fontSize: "0.85rem",
                    },
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") handleCancelMemo();
                  }}
                />
                <Box sx={{ mt: 0.25, display: "flex", alignItems: "center", gap: 0.25 }}>
                  <IconButton size="small" onClick={handleSaveMemo} sx={{ p: 0.125 }}>
                    <CheckIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton size="small" onClick={handleCancelMemo} sx={{ p: 0.125 }}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </>
            ) : memoText ? (
              <Typography
                variant="body2"
                color="text.secondary"
                onClick={handleEditMemoClick}
                sx={{
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.35,
                  fontSize: "0.8rem",
                  cursor: "pointer",
                  borderRadius: 0.5,
                  px: 0.5,
                  mx: -0.5,
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                {memoText}
              </Typography>
            ) : (
              <Typography
                variant="caption"
                color="text.disabled"
                onClick={handleEditMemoClick}
                sx={{
                  cursor: "pointer",
                  "&:hover": { color: "primary.main" },
                }}
              >
                + メモ
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        {task.status === 1 && task.completed_at && (
          <Typography
            variant="caption"
            sx={{ whiteSpace: "nowrap", fontSize: "0.68rem", mb: 0.25 }}
          >
            {formatDate(task.completed_at)}
          </Typography>
        )}
        {enableAssigneeSelection && (
          <UserSelect
            onChange={handleUserSelectChange}
            style={{ fontSize: "14px" }}
            activeStaffList={activeStaffList}
            task={task}
          />
        )}
      </Box>
      <ErrorDialog
        open={showError}
        onClose={handleCloseError}
        errorMessage={errorMessage}
      />
      <WarningDialog open={showWarning} onClose={handleCloseWarning} />
    </ListItem>
  );
}

function TaskList({
  todo,
  setTodo,
  groupId,
  activeStaffList,
  onQueueUpdate,
  onCompleteUpdate,
}) {
  const group = todo.task_groups.find((group) => group.id === groupId);

  return (
    <List>
      {group.tasks.map((task) => (
        <Task
          key={task.id}
          task={task}
          todoPublicId={todo.public_id}
          setTodo={setTodo}
          activeStaffList={activeStaffList}
          drawTopBorder={task.order === 0}
          onQueueUpdate={onQueueUpdate}
          onCompleteUpdate={onCompleteUpdate}
        />
      ))}
    </List>
  );
}

function GroupList({ groupId, groupName, taskList }) {
  return (
    <Paper key={groupId} sx={{ p: 1.5, borderRadius: 2.5 }}>
      <div style={groupListStyle}>
        <Typography style={groupNameStyle}>{groupName}</Typography>
      </div>
      {taskList}
    </Paper>
  );
}

function UseTodo() {
  const [todo, setTodo] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeStaffList, setActiveStaffList] = useState([]);
  const [todayResolveError, setTodayResolveError] = useState("");
  const [pendingUpdateCount, setPendingUpdateCount] = useState(0);
  const { todoPublicId, scheduledOn } = useParams();
  const navigate = useNavigate();
  const config = useConfig();
  const assigneeSelectionEnabled = Boolean(
    config?.features?.enableAssigneeSelection
  );
  const hasPendingUpdates = pendingUpdateCount > 0;

  const getTodayJstYmd = () => {
    // "YYYY-MM-DD" (JST)
    return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Tokyo" });
  };

  const addDaysJstYmd = (ymd, days) => {
    const base = new Date(`${ymd}T00:00:00+09:00`);
    base.setDate(base.getDate() + days);
    return base.toLocaleDateString("en-CA", { timeZone: "Asia/Tokyo" });
  };

  const formatScheduledOn = (ymd) => {
    if (!ymd) return "";
    const [y, m, d] = String(ymd).split("-").map(Number);
    const date = new Date(y, m - 1, d);
    return new Intl.DateTimeFormat("ja-JP", {
      month: "numeric",
      day: "numeric",
      weekday: "short",
    }).format(date);
  };

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setTodayResolveError("");
      try {
        if (assigneeSelectionEnabled) {
          const activeStaff = await getActiveStaff();
          const sortedActiveStaff = activeStaff.sort((a, b) =>
            a.family_name.localeCompare(b.family_name)
          );
          setActiveStaffList(sortedActiveStaff);
        } else {
          setActiveStaffList([]);
        }

        if (!todoPublicId) {
          const requestedYmd = scheduledOn || getTodayJstYmd();
          const resolved = await resolveTodoIdByDate(requestedYmd);
          if (resolved?.todo_public_id) {
            navigate(`/todos/${resolved.todo_public_id}`, { replace: true });
            return;
          }

          setTodo({});
          return;
        }

        const todoData = await fetchTodo(todoPublicId);
        setTodo(sortTasks(todoData));
      } catch (error) {
        console.error("Error fetching data:", error);
        if (!todoPublicId) {
          setTodayResolveError(
            error instanceof Error ? error.message : "Todoの取得に失敗しました。"
          );
        }
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [assigneeSelectionEnabled, todoPublicId, scheduledOn, navigate]);

  if (loading) {
    return <Loading />;
  }

  const todayYmd = getTodayJstYmd();
  const requestedYmd = scheduledOn || todayYmd;
  const currentYmd = todo?.scheduled_on ? String(todo.scheduled_on) : requestedYmd;

  const listPath = (() => {
    const baseYmd = todo?.scheduled_on || requestedYmd;
    const [y, m] = String(baseYmd).split("-");
    return `/todos/${y}/${m}`;
  })();

  const canNavigateNext = currentYmd < todayYmd;

  const headerAction = (
    <Button
      variant="outlined"
      startIcon={<ArrowBackRoundedIcon />}
      onClick={() => navigate(listPath)}
      disabled={hasPendingUpdates}
      sx={{ width: { xs: "100%", md: "auto" }, whiteSpace: "nowrap" }}
    >
      {hasPendingUpdates ? (
        "保存中..."
      ) : (
        <Box component="span" sx={{ display: { xs: "block", sm: "inline" }, lineHeight: 1.15 }}>
          ワークスケジュール
          <Box component="span" sx={{ display: { xs: "block", sm: "inline" } }}>
            一覧へ
          </Box>
        </Box>
      )}
    </Button>
  );

  const dateHeader = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <IconButton
        size="small"
        aria-label="前日へ"
          disabled={hasPendingUpdates}
        onClick={() =>
          navigate(`/todos/by-date/${addDaysJstYmd(currentYmd, -1)}`)
        }
      >
        <ChevronLeftRoundedIcon />
      </IconButton>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          fontWeight: 700,
          minWidth: 0,
          whiteSpace: "nowrap",
        }}
      >
        {formatScheduledOn(currentYmd)}
      </Typography>

      {canNavigateNext ? (
        <IconButton
          size="small"
          aria-label="翌日へ"
          disabled={hasPendingUpdates}
          onClick={() =>
            navigate(`/todos/by-date/${addDaysJstYmd(currentYmd, 1)}`)
          }
        >
          <ChevronRightRoundedIcon />
        </IconButton>
      ) : (
        // keep spacing consistent when hidden
        <Box sx={{ width: 40 }} />
      )}
    </Box>
  );

  if (!todoPublicId && (!todo || !todo.task_groups)) {
    const labelDate = formatScheduledOn(requestedYmd) || requestedYmd;
    return (
      <Stack spacing={2}>
        <PageHeader title="ワークスケジュール" top={dateHeader} action={headerAction} />
        {todayResolveError ? (
          <Alert severity="warning">{todayResolveError}</Alert>
        ) : (
          <Alert severity="info">
            {labelDate}に予定されているワークスケジュールはありません
          </Alert>
        )}
      </Stack>
    );
  }

  if (!todo || !todo.task_groups) {
    return (
      <Alert severity="error">Todoの読み込みに失敗しました。</Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <PageHeader
        title={todo.name}
        top={dateHeader}
        action={headerAction}
      />
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
        }}
      >
        {todo.task_groups.map((group) => (
          <GroupList
            key={group.id}
            groupId={group.id}
            groupName={group.name}
            taskList={
              <TaskList
                todo={todo}
                setTodo={setTodo}
                groupId={group.id}
                activeStaffList={activeStaffList}
                onQueueUpdate={() => setPendingUpdateCount((count) => count + 1)}
                onCompleteUpdate={(count = 1) =>
                  setPendingUpdateCount((current) => Math.max(0, current - count))
                }
              />
            }
          />
        ))}
      </Box>
    </Stack>
  );
}

export default UseTodo;
